import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import LanguageToggle from "@/components/LanguageToggle";
import { LogoIcon, LogoWordmark } from "@/components/Logo";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Check } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { trackEvent, attributeAccount, captureUtm, getAttribution, getPartnerCode, metaEventId, metaTrack, metaMirror } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Signup() {
  const { t, i18n } = useTranslation();
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "", referral_code: params.get("ref") || "" });
  const [trades, setTrades] = useState([]);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [touched, setTouched] = useState({});
  const on = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); trackEvent("signup_started"); };
  const blur = (k) => () => setTouched((p) => ({ ...p, [k]: true }));
  const capsCheck = (e) => { if (e.getModifierState) setCapsOn(e.getModifierState("CapsLock")); };

  useSeo({ title: t("seo_signup_title"), description: t("seo_signup_desc") });

  useEffect(() => { // trade packs are public — shown before an account exists
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trade-packs`)
      .then((r) => (r.ok ? r.json() : { packs: [] }))
      .then((d) => setPacks(d.packs || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    captureUtm(); // pick up utm_*/fbclid carried through from the landing CTA
    const id = metaEventId();
    metaTrack("Lead", id);   // signup form opened
    metaMirror("Lead", id);
  }, []);

  // Already signed in with a valid session → never show the signup form.
  if (user) return <Navigate to="/app" replace />;

  const emailValid = EMAIL_RE.test(form.email);
  const pwValid = form.password.length >= 8;
  const pwScore = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p) || p.length >= 16) s++;
    return Math.min(3, Math.max(0, s - 1));
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // duplicate-submission guard
    if (!emailValid || !pwValid || !form.name.trim() || !form.company_name.trim()) {
      setTouched({ email: true, password: true, name: true, company_name: true });
      return;
    }
    setBusy(true);
    const regEventId = metaEventId();
    const res = await register({ ...form, trades, meta_event_id: regEventId, attribution: getAttribution().last_touch,
      partner_code: (params.get("via") || getPartnerCode() || "").toUpperCase() || undefined });
    setBusy(false);
    if (res.ok) {
      metaTrack("CompleteRegistration", regEventId);
      trackEvent("signup_completed");
      trackEvent("trial_activated");
      attributeAccount();
      toast.success(t("toast_account_created"));
      toast.info(t("signup_welcome_sending", { email: form.email.trim() }), { duration: 6000 });
      navigate("/app");
    }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen bg-slateink bf-grain flex flex-col">
      <div className="flex items-center justify-between px-5 py-5">
        <Link to="/" className="flex items-center gap-2 text-white font-bold" data-testid="link-home">
          <LogoIcon size={36} />
          <LogoWordmark dark className="text-base" />
        </Link>
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-5 py-8">
        <form onSubmit={submit} noValidate className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 md:p-8">
          <h1 className="font-heading text-2xl font-extrabold text-white">{t("auth_register_title")}</h1>
          <Field label={t("auth_name")} htmlFor="signup-name">
            <Input id="signup-name" name="name" autoComplete="name" data-testid="input-name" required value={form.name} onChange={on("name")} onBlur={blur("name")} className="h-12 bg-slate-950 border-slate-700 text-white" />
          </Field>
          <Field label={t("auth_company")} htmlFor="signup-company">
            <Input id="signup-company" name="organization" autoComplete="organization" data-testid="input-company" required value={form.company_name} onChange={on("company_name")} onBlur={blur("company_name")} className="h-12 bg-slate-950 border-slate-700 text-white" />
          </Field>
          {packs.length > 0 && (
            <Field label={t("signup_trades_label")} htmlFor="signup-trades"
              hint={<span className="text-[11px] font-semibold text-slate-500">{t("signup_trades_hint")}</span>}>
              <div className="flex flex-wrap gap-2" data-testid="signup-trades">
                {packs.map((p) => {
                  const on_ = trades.includes(p.key);
                  return (
                    <button type="button" key={p.key} data-testid={`signup-trade-${p.key}`} aria-pressed={on_}
                      onClick={() => setTrades((cur) => (on_ ? cur.filter((x) => x !== p.key) : [...cur, p.key]))}
                      className={`min-h-[44px] rounded-full border px-4 text-xs font-bold transition-colors active:scale-95 ${on_ ? "border-brand bg-brand text-white" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-brand/60"}`}>
                      {i18n.language?.startsWith("es") ? p.es : p.en}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
          <Field label={t("auth_email")} htmlFor="signup-email"
            hint={touched.email && !emailValid ? <span className="text-xs font-semibold text-red-400" data-testid="signup-email-error">{t("auth_email_invalid")}</span> : null}>
            <Input id="signup-email" name="email" autoComplete="email" inputMode="email" data-testid="input-email" type="email" required value={form.email} onChange={on("email")} onBlur={blur("email")}
              aria-invalid={touched.email && !emailValid}
              className={`h-12 bg-slate-950 border-slate-700 text-white ${touched.email && !emailValid ? "border-red-500" : ""}`} />
          </Field>
          <Field label={t("auth_password")} htmlFor="signup-password"
            hint={
              <span className={`inline-flex items-center gap-1 text-xs font-semibold ${form.password && pwValid ? "text-green-400" : "text-slate-400"}`} data-testid="signup-pw-req">
                <Check className={`h-3 w-3 ${form.password && pwValid ? "" : "opacity-40"}`} /> {t("auth_pw_req")}
              </span>
            }>
            <div className="relative">
              <Input id="signup-password" name="new-password" autoComplete="new-password" data-testid="input-password" type={showPw ? "text" : "password"} required minLength={8} maxLength={128}
                value={form.password} onChange={on("password")} onBlur={blur("password")}
                onKeyDown={capsCheck} onKeyUp={capsCheck}
                className="h-12 bg-slate-950 border-slate-700 text-white pr-12" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? t("auth_pw_hide") : t("auth_pw_show")} data-testid="signup-toggle-pw"
                className="absolute right-1 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-slate-400 hover:text-white transition-colors">
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {capsOn && (
              <div className="text-xs font-semibold text-amber-400" data-testid="signup-caps-warning" role="status">{t("auth_caps_lock")}</div>
            )}
            {form.password && (
              <div className="flex items-center gap-2" data-testid="signup-pw-strength">
                <div className="h-1.5 flex-1 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${["w-1/4 bg-red-500", "w-2/4 bg-amber-500", "w-3/4 bg-lime-500", "w-full bg-green-500"][pwScore]}`} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("auth_pw_strength")}: {t(["auth_pw_weak", "auth_pw_fair", "auth_pw_good", "auth_pw_strong"][pwScore])}</span>
              </div>
            )}
          </Field>
          {showReferral || form.referral_code ? (
            <Field label={t("auth_referral_code")} htmlFor="signup-referral">
              <Input id="signup-referral" name="referral_code" data-testid="input-referral" value={form.referral_code} onChange={on("referral_code")} placeholder={t("auth_referral_placeholder")} className="h-12 bg-slate-950 border-slate-700 text-white font-mono uppercase" />
            </Field>
          ) : (
            <button type="button" onClick={() => setShowReferral(true)} data-testid="signup-referral-toggle"
              className="inline-flex min-h-[44px] items-center text-xs font-semibold text-brand hover:underline">
              {t("auth_referral_link")}
            </button>
          )}
          <button data-testid="btn-signup" disabled={busy} className="bf-thumb-cta inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand font-bold text-white bf-brand-shadow transition-transform active:scale-95 disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("action_create_account")}
          </button>
          <div className="flex items-center gap-3" data-testid="auth-divider">
            <div className="h-px flex-1 bg-slate-700" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t("auth_or")}</span>
            <div className="h-px flex-1 bg-slate-700" />
          </div>
          <GoogleAuthButton testid="google-signup-btn" />
          <p className="text-center text-[11px] font-semibold text-slate-400" data-testid="signup-reassurance">{t("auth_signup_reassurance")}</p>
          <p className="text-center text-[11px] leading-relaxed text-slate-500" data-testid="signup-legal-notice">
            {t("auth_terms_notice_pre")}
            <Link to="/terms" data-testid="signup-terms-link" className="text-brand font-semibold hover:underline">{t("auth_terms_link")}</Link>
            {t("auth_terms_notice_mid")}
            <Link to="/privacy" data-testid="signup-privacy-link" className="text-brand font-semibold hover:underline">{t("auth_privacy_link")}</Link>
            {t("auth_terms_notice_post")}
          </p>
          <div className="text-sm text-slate-400 text-center">
            {t("auth_have_account")} <Link to="/login" className="text-brand font-semibold hover:underline" data-testid="link-login">{t("action_signin")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children, hint }) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-widest text-slate-400">{label}</label>
      {children}
      {hint}
    </div>
  );
}
